import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./PropertyMap.css";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Buildings, CaretRight, ShieldCheck, Wrench, Warning, ArrowSquareOut, ListBullets, HardHat, Lightning, Phone, X, House, MapPin, Clock, NavigationArrow } from "@phosphor-icons/react";

const COLOUR_LABEL = { red: "Expired compliance or an open emergency", amber: "Expiring / missing certificate or open jobs", green: "All clear — valid compliance, no open work", grey: "No compliance set up, no open work" };
const COMPLIANCE_LABEL = { expired: "Expired", expiring: "Expiring soon", missing: "Certificate missing", valid: "All valid", none: "Not set up" };
const COMPLIANCE_COLOUR = { expired: "red", expiring: "amber", missing: "amber", valid: "green", none: "grey" };
const SOURCE = {
  live: { label: "Live", cls: "bg-emerald-500/15 text-emerald-800 dark:text-emerald-300", Icon: NavigationArrow },
  on_site: { label: "On site now", cls: "bg-amber-500/15 text-amber-800 dark:text-amber-300", Icon: Wrench },
  last_seen: { label: "Last seen", cls: "bg-sky-500/15 text-sky-800 dark:text-sky-300", Icon: Clock },
  home: { label: "Home base", cls: "bg-emerald-500/15 text-emerald-800 dark:text-emerald-300", Icon: House },
};

const pinIcon = (p, dim) => L.divIcon({
  className: "",
  html: `<div class="fx-pin fx-pin-${p.colour}${p.emergencies ? " fx-pin-alert" : ""}${dim ? " fx-pin-dim" : ""}" title="${p.name.replace(/"/g, "&quot;")}">${p.open_jobs + p.emergencies ? `<span class="fx-pin-badge">${p.open_jobs + p.emergencies}</span>` : ""}</div>`,
  iconSize: [26, 26], iconAnchor: [13, 13], popupAnchor: [0, -14],
});

const engineerIcon = (e) => L.divIcon({
  className: "",
  html: `<div class="fx-eng${e.rank === 1 ? " fx-eng-nearest" : ""}${e.source === "live" ? " fx-eng-live" : ""}" title="${e.name.replace(/"/g, "&quot;")}">${e.rank}</div>`,
  iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -14],
});

function FitBounds({ points, extra }) {
  const map = useMap();
  useEffect(() => {
    const all = [...points.map((p) => [p.lat, p.lng]), ...(extra || []).map((e) => [e.lat, e.lng])];
    if (!all.length) return;
    const withPanel = !!(extra && extra.length);
    if (withPanel) map.closePopup(); // an open popup's auto-pan would otherwise undo the fit
    map.fitBounds(L.latLngBounds(all).pad(0.15), { maxZoom: 15, paddingTopRight: withPanel ? [360, 20] : [0, 0], paddingBottomLeft: withPanel ? [20, 20] : [0, 0] });
  }, [map, points, extra]);
  return null;
}

const ago = (iso) => {
  if (!iso) return "";
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 60 ? `${m} min ago` : `${Math.round(m / 60)} h ago`;
};
const miles = (e) => (e.miles == null ? "—" : `${e.miles} mi${e.distance_kind === "straight" ? " (straight line)" : ""}`);
const eta = (e) => {
  if (e.minutes == null) return "";
  const h = Math.floor(e.minutes / 60), m = e.minutes % 60;
  const t = e.minutes < 1 ? "<1 min" : h ? `${h} h${m ? ` ${m} min` : ""}` : `${m} min`;
  return `${e.distance_kind === "straight" ? "est. " : ""}${t} drive`;
};

/** Floating list of in-house engineers ranked by driving distance to one property, with one-tap emergency dispatch. */
function NearestEngineersPanel({ data, loading, property, onClose }) {
  const navigate = useNavigate();
  const dispatch = (e) => navigate(`/emergency-jobs?new=1&client_id=${property.client_id}&property_id=${property.id}${e ? `&operative_id=${e.id}` : ""}`);
  return (
    <div className="absolute top-3 right-3 z-[600] w-[21rem] max-w-[calc(100%-1.5rem)] rounded-lg border border-border bg-card/95 backdrop-blur shadow-xl text-xs" data-testid="nearest-engineers-panel">
      <div className="flex items-start gap-2 px-3 py-2 border-b border-border">
        <HardHat size={16} className="text-primary mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm leading-tight">Nearest engineers</p>
          <p className="text-muted-foreground truncate" data-testid="nearest-engineers-property">{property.name} · {property.postcode}</p>
        </div>
        <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close" data-testid="nearest-engineers-close"><X size={14} /></button>
      </div>
      <div className="max-h-[400px] overflow-y-auto">
        {loading && <p className="px-3 py-4 text-muted-foreground" data-testid="nearest-engineers-loading">Working out driving distances…</p>}
        {data && !data.located && <p className="px-3 py-3 text-amber-700" data-testid="nearest-engineers-unlocated">{data.message}</p>}
        {data && data.located && data.engineers.length === 0 && <p className="px-3 py-3 text-muted-foreground" data-testid="nearest-engineers-none">No in-house engineer has a known position yet.</p>}
        {data?.engineers.map((e) => {
          const src = SOURCE[e.source];
          return (
            <div key={e.id} className={`px-3 py-2 border-b border-border/70 ${e.rank === 1 ? "bg-primary/5" : ""}`} data-testid={`nearest-engineer-${e.id}`}>
              <div className="flex items-center gap-2">
                <span className={`fx-eng fx-eng-inline${e.rank === 1 ? " fx-eng-nearest" : ""}`}>{e.rank}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm leading-tight truncate">{e.name}</p>
                  <p className="text-muted-foreground flex items-center gap-1 flex-wrap"><MapPin size={11} /> <span className="font-medium text-foreground" data-testid={`nearest-engineer-miles-${e.id}`}>{miles(e)}</span>
                    {eta(e) && <span className="inline-flex items-center gap-0.5 font-semibold text-foreground" data-testid={`nearest-engineer-eta-${e.id}`}>· <Clock size={11} /> {eta(e)}</span>}
                    {e.source === "on_site" ? <span className="text-amber-700"> · busy on site</span> : null}</p>
                </div>
                <Button size="sm" className="h-7 text-[11px] px-2 bg-red-600 hover:bg-red-700 text-white shrink-0" onClick={() => dispatch(e)} data-testid={`nearest-engineer-dispatch-${e.id}`}><Lightning size={12} weight="fill" className="mr-1" /> Dispatch</Button>
              </div>
              <div className="mt-1.5 flex items-center gap-1.5 flex-wrap pl-8">
                <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${src.cls}`}><src.Icon size={10} /> {src.label}{e.source === "last_seen" ? ` ${ago(e.at)}` : e.source === "live" ? ` · ${e.age_minutes ? `${e.age_minutes} min ago` : "just now"}` : e.source === "on_site" && e.on_site_minutes != null ? ` · ${e.on_site_minutes} min` : ""}</span>
                {e.phone && <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`nearest-engineer-phone-${e.id}`}><Phone size={10} /> {e.phone}</a>}
              </div>
              <p className="text-[11px] text-muted-foreground pl-8 mt-0.5 truncate" title={e.detail}>{e.detail}</p>
            </div>
          );
        })}
        {data && data.no_location.length > 0 && (
          <div className="px-3 py-2 text-[11px] text-muted-foreground" data-testid="nearest-engineers-no-location">
            No position for {data.no_location.map((n) => n.name).join(", ")} — set a home address in Team › In-House so they can be ranked.
          </div>
        )}
      </div>
      <div className="px-3 py-2 border-t border-border flex items-center justify-between gap-2">
        <span className="text-[10px] text-muted-foreground">Driving miles via OpenStreetMap routing.</span>
        <Button size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={() => dispatch(null)} data-testid="nearest-engineers-dispatch-unassigned"><Lightning size={11} className="mr-1" /> Raise emergency here</Button>
      </div>
    </div>
  );
}

/** Every property for a client (main = whole family) — or the whole portfolio — as coloured pins. */
export default function PropertyMap({ clientId, onShowInList }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [near, setNear] = useState(null); // {property, data?, loading}

  useEffect(() => {
    setData(null); setError(false); setNear(null);
    API.get("/properties/map", { params: { client_id: clientId || "" } }).then((r) => setData(r.data)).catch(() => setError(true));
  }, [clientId]);

  const points = useMemo(() => data?.points || [], [data]);
  const s = data?.summary || {};
  const engineers = useMemo(() => near?.data?.engineers || [], [near]);
  const focus = near?.property;

  const findEngineers = async (p) => {
    setNear({ property: p, loading: true });
    try { const { data: d } = await API.get(`/properties/${p.id}/nearest-engineers`); setNear({ property: p, data: d, loading: false }); }
    catch (e) { setNear(null); toast.error(formatApiError(e.response?.data?.detail) || "Could not rank engineers"); }
  };

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="property-map">
      <div className="px-3 py-2 border-b border-border bg-muted/30 flex items-center gap-3 flex-wrap text-xs" data-testid="property-map-summary">
        {data ? <span className="font-medium">{s.placed} of {s.total} {s.total === 1 ? "property" : "properties"} on the map</span> : <span>{error ? "Couldn't load the map" : "Placing properties…"}</span>}
        {data && <span className="text-muted-foreground">· {s.open_jobs || 0} open jobs · {s.emergencies || 0} emergencies</span>}
        <div className="ml-auto flex items-center gap-2 flex-wrap" data-testid="property-map-legend">
          {["red", "amber", "green", "grey"].map((c) => <span key={c} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" title={COLOUR_LABEL[c]}><i className={`fx-legend-dot fx-pin-${c}`} /> {s[c] ?? 0}</span>)}
          {engineers.length > 0 && <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" title="In-house engineers ranked by distance"><i className="fx-legend-dot fx-eng-dot" /> engineers</span>}
        </div>
      </div>
      <div className="h-[520px] relative" data-testid="property-map-canvas">
        {data && points.length === 0 ? (
          <div className="absolute inset-0 z-[500] flex items-center justify-center text-sm text-muted-foreground bg-background/80 p-6 text-center" data-testid="property-map-empty">{s.total ? "None of these properties have a postcode we could place." : "No properties to show here yet."}</div>
        ) : null}
        {near && <NearestEngineersPanel data={near.data} loading={near.loading} property={near.property} onClose={() => setNear(null)} />}
        {/* keyboard=false keeps the map and pins un-focusable: focusing them scrolled the page under the pointer and swallowed the click */}
        <MapContainer center={[54.2, -2.5]} zoom={6} scrollWheelZoom keyboard={false} className="h-full w-full">
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitBounds points={focus && engineers.length ? [focus] : points} extra={engineers} />
          {focus && engineers.map((e) => (
            <Polyline key={`line-${e.id}`} positions={[[focus.lat, focus.lng], [e.lat, e.lng]]} pathOptions={{ color: e.rank === 1 ? "#135DD4" : "#64748b", weight: e.rank === 1 ? 3 : 1.5, dashArray: e.rank === 1 ? null : "6 6", opacity: 0.8 }} />
          ))}
          {engineers.map((e) => (
            <Marker key={`eng-${e.id}`} position={[e.lat, e.lng]} icon={engineerIcon(e)} keyboard={false} zIndexOffset={1000}>
              <Popup minWidth={200}>
                <div className="space-y-1" data-testid={`engineer-map-popup-${e.id}`}>
                  <p className="font-semibold text-sm leading-tight">#{e.rank} {e.name}</p>
                  <p className="text-xs"><span className="font-medium">{miles(e)}</span> to {focus?.name}</p>
                  <p className="text-[11px] text-muted-foreground">{e.detail}</p>
                </div>
              </Popup>
            </Marker>
          ))}
          {points.map((p) => (
            <Marker key={p.id} position={[p.lat, p.lng]} icon={pinIcon(p, !!(focus && focus.id !== p.id))} keyboard={false}>
              <Popup minWidth={240}>
                <div className="space-y-1.5" data-testid={`property-map-popup-${p.id}`}>
                  <p className="font-semibold text-sm leading-tight">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.address}</p>
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Buildings size={11} /> {p.parent_name ? <>{p.parent_name} <CaretRight size={10} /> {p.client_name}</> : p.client_name}</p>
                  <p className="text-xs flex items-center gap-1"><Wrench size={12} /> {p.open_jobs} open job{p.open_jobs === 1 ? "" : "s"}{p.emergencies ? <span className="text-red-600 font-medium inline-flex items-center gap-0.5 ml-1"><Warning size={12} weight="fill" /> {p.emergencies} emergenc{p.emergencies === 1 ? "y" : "ies"}</span> : null}</p>
                  <p className="text-xs flex items-center gap-1"><ShieldCheck size={12} /> Compliance: <span className={`font-medium fx-text-${COMPLIANCE_COLOUR[p.compliance.status]}`}>{COMPLIANCE_LABEL[p.compliance.status]}</span>{p.compliance.total ? <span className="text-muted-foreground"> · {p.compliance.valid} valid, {p.compliance.expiring} expiring, {p.compliance.expired} expired</span> : null}</p>
                  <div className="flex items-center gap-1 pt-1 flex-wrap">
                    <Button size="sm" className="h-6 text-[11px] px-2" onClick={() => findEngineers(p)} data-testid={`property-map-engineers-${p.id}`}><HardHat size={11} className="mr-1" /> Nearest engineer</Button>
                    {onShowInList && <Button size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={() => onShowInList(p)} data-testid={`property-map-show-${p.id}`}><ListBullets size={11} className="mr-1" /> Show in list</Button>}
                    <Button asChild size="sm" variant="ghost" className="h-6 text-[11px] px-2"><Link to={`/compliance/properties/${p.id}`} data-testid={`property-map-compliance-${p.id}`}><ShieldCheck size={11} className="mr-1" /> Compliance</Link></Button>
                    <Button asChild size="sm" variant="ghost" className="h-6 text-[11px] px-2"><Link to={`/clients/${p.client_id}`} data-testid={`property-map-client-${p.id}`}><ArrowSquareOut size={11} className="mr-1" /> Client</Link></Button>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      {data && data.unplaced.length > 0 && (
        <div className="px-3 py-2 border-t border-border text-xs" data-testid="property-map-unplaced">
          <span className="font-medium">Couldn't place {data.unplaced.length}:</span>{" "}
          {data.unplaced.map((p, i) => <span key={p.id}>{i > 0 && ", "}<span className="text-muted-foreground">{p.name}{p.postcode ? ` (${p.postcode})` : " (no postcode)"}</span></span>)}
          <span className="text-muted-foreground"> — add or correct the postcode on the property to pin it.</span>
        </div>
      )}
    </div>
  );
}
